<template>
  <div>
    <v-breadcrumbs :items="items"></v-breadcrumbs>
    <v-card class="mb-5">
      <v-card-title>
        <v-container>
          <v-row>
            <v-col cols="12" md="2" class="mr-5 mb-1">
              Invoice Refund
            </v-col>
          </v-row>
        </v-container>
      </v-card-title>
    </v-card>
    <v-card :loading="loadingStatus">
      <v-container>
        <v-row>
          <!-- Left Column: Form Fields -->
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="headline">Refund To Customer</v-card-title>
              <v-card-text>
                <v-form ref="form" lazy-validation>
                  <!-- Invoice ID -->
                  <v-text-field
                      label="Invoice ID"
                      v-model="submitForm.invoiceId"
                      outlined
                      dense
                      required
                      readonly
                  ></v-text-field>

                  <!-- Recommender Name -->
                  <v-autocomplete
                      label="Recommender Name"
                      v-model="submitForm.recommenderDoctor"
                      item-text="dr_name"
                      item-value="id"
                      :items="doctors"
                      outlined
                      dense
                      :loading="doctorLoadingStatus"
                  ></v-autocomplete>

                  <v-select
                      label="Refund Type"
                      v-model="submitForm.refundType"
                      :items="refundTypes"
                      outlined
                      dense
                      required
                  ></v-select>

                  <v-text-field
                      label="Refund Amount"
                      v-model="submitForm.refundAmount"
                      type="number"
                      outlined
                      dense
                      required
                  ></v-text-field>

                  <v-textarea
                      label="Note"
                      v-model="submitForm.note"
                      outlined
                      dense
                      rows="2"
                  ></v-textarea>

                  <div v-if="spinnerButtons">
                    <v-progress-circular :width="12" size="61" color="green" indeterminate
                                         class="mt-5 mb-10"></v-progress-circular>
                  </div>
                  <v-btn color="primary" class="mt-4" :disabled="btnSubmit" @click="submitInvoiceRefund">Submit</v-btn>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Column: Invoice Details -->
          <v-col cols="12" md="6">
            <!-- Invoice Information -->
            <v-card outlined class="mb-4">
              <!-- Invoice Details -->
              <v-card-title class="headline">Invoice Information: {{ invoiceData.invoice_id }}  <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                      @click="invoice_print()"
                      dark
                      v-bind="attrs"
                      v-on="on"
                      class="ma-2"
                      open
                      prepend-icon="mdi-printer"
                      color="warning"
                      label
                      outlined
                  >
                    <v-icon> mdi-printer </v-icon>
                  </v-chip>
                </template>
                <span>Invoice Print</span>
              </v-tooltip></v-card-title>
              <v-card-text>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Invoice Type:</v-list-item-title>
                      <v-list-item-subtitle> <strong>{{ invoiceData.invoice_type }}</strong>
                        <v-chip
                            v-if="invoiceData.consultation_type"
                          class="ma-2"
                          color="green"
                          outlined
                          x-small
                      >
                          {{ invoiceData.consultation_type }}
                      </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Create Date:</v-list-item-title>
                      <v-list-item-subtitle>{{ invoiceData.inv_date }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Discount Amount:</v-list-item-title>
                      <v-list-item-subtitle>{{ invoiceData.discount_amount || '0.00' }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-if="invoiceData.cash_back_amount !== '0.00'">
                    <v-list-item-content>
                      <v-list-item-title>Cashback Amount:</v-list-item-title>
                      <v-list-item-subtitle>{{ invoiceData.cash_back_amount || '0.00' }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Paid Amount:</v-list-item-title>
                      <v-list-item-subtitle>{{ invoiceData.paid_amount || '0.00' }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Doctor Amount:</v-list-item-title>
                      <v-list-item-subtitle>{{ invoiceData.doctor_payable || '0.00' }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Hospital Amount:</v-list-item-title>
                      <v-list-item-subtitle>{{ invoiceData.hospital_payable || '0.00' }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>

              <!-- Doctor Information -->
              <v-card-subtitle class="mt-0">Doctor Information </v-card-subtitle>
              <v-card-text class="py-0" v-if="invoiceData.doctor">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content v-if="invoiceData.doctor">
                      <v-list-item-title>Doctor Name:</v-list-item-title>
                      <v-list-item-subtitle>{{ invoiceData.doctor.dr_name  }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
              <v-card-text class="py-0" v-if="invoiceData.refer_doctor">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content v-if="invoiceData.refer_doctor">
                      <v-list-item-title>Refer Doctor Name:</v-list-item-title>
                      <v-list-item-subtitle>{{  invoiceData.refer_doctor.dr_name }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>


              <!-- Patient Information -->
              <v-card-subtitle v-if="invoiceData.patient" class="mt-0">Patient Information: {{ invoiceData.patient.patient_id }}</v-card-subtitle>
              <v-card-text class="py-0" v-if="invoiceData.patient">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Patient Name:</v-list-item-title>
                      <v-list-item-subtitle>{{ invoiceData.patient.patient_name }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>

              <!-- Refund Information -->
              <v-card-subtitle v-if="invoiceData.invoice_refund_data && invoiceData.invoice_refund_data.length > 0"
                               class="mt-2">
                Previous Refund Information
              </v-card-subtitle>
              <v-card-text v-if="invoiceData.invoice_refund_data && invoiceData.invoice_refund_data.length > 0"
                           class="py-1">
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                    <tr>
                      <th>#</th>
                      <th>Refund Date</th>
                      <th>Refund Amount</th>
                      <th>Refund Type</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(refund, index) in invoiceData.invoice_refund_data" :key="index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ refund.refund_date }}</td>
                      <td>{{ refund.refund_amount || '0.00' }}</td>
                      <td>{{ refund.refund_type || 'N/A' }}</td>
                    </tr>
                    <tr >
                      <td></td>
                      <td><strong>Total Refund</strong></td>
                      <td><strong>{{ totalRefundSum || '0.00' }}</strong></td>
                      <td></td>
                    </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>

        </v-row>
      </v-container>
    </v-card>
  </div>
</template>

<script>
import atshq from "../../../js/setup";

export default {
  data() {
    return {
      items: [
        {
          text: "Dashboard",
          disabled: false,
          href: "/",
        },
        {
          text: "Invoice refund",
          disabled: true,
        },
      ],
      spinnerButtons: true,
      btnSubmit: false,
      loadingStatus: true,
      doctorLoadingStatus: true,
      invoiceData: [],
      doctors: [],
      invoice_id: this.$route.params.id,
      returnApiBaseUrl: atshq.defaults.headers.common.api_base_URL,
      submitForm: {
        invoiceId: '',
        recommenderDoctor: '',
        refundType: '',
        refundAmount: 0,
        note: '',
        user_id: localStorage.getItem("userId"),
      },

      refundTypes: [],
      totalRefundSum: 0,
    };
  },
  created() {
    this.initialize();
    this.getDoctors();
  },
  methods: {
    initialize() {
      atshq
          .get("invoices/invoice-refund/" + this.invoice_id)
          .then((response) => {
            this.invoiceData = response.data.invoiceInfo;
            this.refundTypes = response.data.RefundTypes;
            this.totalRefundSum = response.data.totalRefundSum;
            if (this.invoiceData.invoice_type === 'investigation'){
              this.submitForm.recommenderDoctor = this.invoiceData.refer_doctor.doctor_id
            }
            this.loadingStatus = false;
            this.spinnerButtons = false;
            this.getSubmitData(this.invoiceData);
          })
          .catch((e) => {
            this.$noty.error(e.response.data.message);
          });
    },
    getDoctors() {
      atshq
          .get("/doctors")
          .then((response) => {
            this.doctors = response.data.data;
            this.doctorLoadingStatus = false
          })
          .catch((e) => {
            this.errors = true;
            this.errorMessage = e;
          });
    },
    getSubmitData(invoiceData){
      this.submitForm.invoiceId=invoiceData.invoice_id;
      this.submitForm.recommenderDoctor= invoiceData.doctor.id ;
    },
    refreshSubmitData(){
      this.submitForm.note='';
      this.submitForm.recommenderDoctor='';
      this.submitForm.refundType='';
      this.submitForm.refundAmount=0;
    },
    submitInvoiceRefund() {
      if (this.submitForm.refundAmount === null || this.submitForm.refundAmount === '' || this.submitForm.refundAmount <= 0) {
        this.$noty.error('Refund amount cannot be less than or equal to 0 or empty.');
        return;
      }
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        type: 'success',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, submit it!'
      }).then((result) => {
        if (result.value) {
          this.btnSubmit = true
          this.spinnerButtons = true;
          this.loadingStatus = true
          let submitUrl = "invoices/invoice-refund-save";
          atshq
              .post(submitUrl, {submitData: this.submitForm})
              .then((response) => {
                this.$noty.success(response.data.message);
                this.spinnerButtons = false;
                this.btnSubmit = false;
                this.initialize();
                this.refreshSubmitData();
                this.loadingStatus = false
              })
              .catch((e) => {
                this.btnSubmit = false
                this.$noty.error(e.response.data.message);
                this.spinnerButtons = false;
                this.initialize();
                this.refreshSubmitData();
              });
        }
      });
    },
    invoice_print() {
      window.open(this.returnApiBaseUrl + "print/invoice/" + this.invoiceData.invoice_id +"?user_id="+ this.submitForm.user_id);
    },
  },
};
</script>

<style scoped>
.headline {
  font-weight: bold;
  color: #2c3e50;
}
</style>